<template>
  <ModalComponent
    ref="modalRef"
    size="xl"
    :submit-button-text="$t('common.close')"
    :hide-submit-button="true"
    @close="handleClose"
  >
    <template #title>
      {{ $t("cancellation.details_title") }}
    </template>

    <template #body>
      <div class="modal-body-content">
        <!-- Loading state -->
        <LoadingIndicatorComponent v-if="isLoading" />

        <!-- Error state -->
        <div v-else-if="error" class="alert alert-danger">
          {{ error }}
        </div>

        <!-- Details content -->
        <div v-else-if="cancellationDetails">
          <!-- Admin ID Section -->
          <div v-if="is_admin" class="mb-3">
            <div
              class="alert alert-subtle-warning mb-0 d-inline-block py-2 px-3"
            >
              <strong>{{ $t("cancellation.table.id") }}:</strong> #{{
                cancellationDetails.id
              }}
            </div>
            <div
              v-if="cancellationDetails.company_order_id"
              class="alert alert-subtle-warning mb-0 d-inline-block py-2 px-3 ms-2"
            >
              <strong>{{ $t("cancellation.details.order_id") }}:</strong> #{{
                cancellationDetails.company_order_id
              }}
            </div>
          </div>

          <!-- Main details row -->
          <div class="row g-3">
            <!-- Left column: Statement Information -->
            <div class="col-lg-6">
              <div class="card border-0 bg-light h-100">
                <div class="card-body">
                  <h6
                    class="card-title text-primary fw-bold mb-3 pb-2 border-bottom border-primary"
                  >
                    {{ $t("cancellation.details.basic_info") }}
                  </h6>
                  <table class="table table-sm table-borderless mb-0">
                    <tbody>
                      <!-- 1. Beneficiary Name -->
                      <tr>
                        <td class="text-muted pe-3" style="width: 45%">
                          {{ $t("cancellation.table.beneficiary_name") }}
                        </td>
                        <td class="fw-semibold">
                          {{ cancellationDetails.beneficiary_name }}
                        </td>
                      </tr>

                      <!-- 2. Registry Code -->
                      <tr v-if="cancellationDetails.registry_code">
                        <td class="text-muted pe-3">
                          {{ $t("go_cancel.form.registry_code.label") }}
                        </td>
                        <td>{{ cancellationDetails.registry_code }}</td>
                      </tr>

                      <!-- 3. Address -->
                      <tr v-if="cancellationDetails.address">
                        <td class="text-muted pe-3">
                          {{ $t("go_cancel.form.address.label") }}
                        </td>
                        <td>{{ cancellationDetails.address }}</td>
                      </tr>

                      <!-- 4. Period -->
                      <tr>
                        <td class="text-muted pe-3">
                          {{ $t("cancellation.details.period") }}
                        </td>
                        <td>
                          {{ formatDate(cancellationDetails.period_start) }} -
                          {{ formatDate(cancellationDetails.period_end) }}
                        </td>
                      </tr>

                      <!-- 5. MWh Cancelled -->
                      <tr>
                        <td class="text-muted pe-3">
                          {{ $t("cancellation.table.mwh_cancelled") }}
                        </td>
                        <td class="fw-bold">
                          {{ cancellationDetails.mwh_cancelled }} MWh
                        </td>
                      </tr>

                      <!-- 6. Beneficiary Type -->
                      <tr>
                        <td class="text-muted pe-3">
                          {{ $t("go_cancel.form.beneficiary_type.label") }}
                        </td>
                        <td>
                          <span class="badge bg-secondary">
                            {{
                              formatBeneficiaryType(
                                cancellationDetails.beneficiary_type
                              )
                            }}
                          </span>
                        </td>
                      </tr>

                      <!-- 7. Purpose -->
                      <tr>
                        <td class="text-muted pe-3">
                          {{ $t("cancellation.table.purpose") }}
                        </td>
                        <td>
                          {{ cancellationDetails.cancellation_purpose || "-" }}
                        </td>
                      </tr>

                      <!-- 8. Registry Document URL -->
                      <tr
                        v-if="cancellationDetails.cancellation_pdf_registry_url"
                      >
                        <td class="text-muted pe-3">
                          {{ $t("cancellation.details.registry_document") }}
                        </td>
                        <td>
                          <a
                            :href="
                              cancellationDetails.cancellation_pdf_registry_url
                            "
                            target="_blank"
                            rel="noopener noreferrer"
                            class="btn btn-sm btn-outline-primary"
                          >
                            <font-awesome-icon
                              :icon="['fas', 'external-link-alt']"
                              class="me-1"
                            />
                            {{
                              $t("cancellation.details.view_registry_document")
                            }}
                          </a>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- Right column: Documents -->
            <div class="col-lg-6">
              <div class="card border-0 bg-light h-100">
                <div class="card-body">
                  <h6
                    class="card-title text-primary fw-bold mb-3 pb-2 border-bottom border-primary"
                  >
                    <font-awesome-icon
                      :icon="['fas', 'file-alt']"
                      class="me-2"
                    />
                    {{ $t("cancellation.details.documents") }}
                  </h6>

                  <!-- Documents list -->
                  <div
                    v-if="cancellationDetails.uploaded_documents?.length > 0"
                    class="d-flex flex-column gap-2"
                  >
                    <div
                      v-for="(
                        doc, index
                      ) in cancellationDetails.uploaded_documents"
                      :key="doc.id"
                      class="card border shadow-sm"
                    >
                      <div class="card-body p-3">
                        <div
                          class="d-flex align-items-center justify-content-between flex-wrap gap-2"
                        >
                          <div
                            class="d-flex align-items-start gap-3 flex-grow-1"
                          >
                            <div class="fs-3 text-muted">
                              <font-awesome-icon
                                :icon="getFileIcon(doc.file_url)"
                              />
                            </div>
                            <div class="flex-grow-1">
                              <div class="fw-medium mb-1">
                                {{ extractFilenameFromUrl(doc.file_url) }}
                              </div>
                              <div class="small text-muted">
                                {{ $t("common.uploaded") }}:
                                {{ formatDate(doc.created_at) }}
                              </div>
                              <div v-if="is_admin" class="mt-1">
                                <span
                                  class="alert alert-subtle-warning p-1 fs-9 mb-0 d-inline-block"
                                >
                                  ID: {{ doc.id }}
                                </span>
                              </div>
                            </div>
                          </div>
                          <button
                            class="btn btn-sm btn-outline-primary"
                            @click="downloadDocument(doc, Number(index))"
                            :disabled="isDownloading[Number(index)]"
                          >
                            <span
                              v-if="isDownloading[Number(index)]"
                              class="spinner-border spinner-border-sm me-1"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            <font-awesome-icon
                              v-else
                              :icon="['fas', 'download']"
                              class="me-1"
                            />
                            {{ $t("common.download") }}
                          </button>
                          <button
                            v-if="is_admin"
                            type="button"
                            class="btn btn-sm"
                            :class="
                              confirmRemovalId === doc.id
                                ? 'btn-danger'
                                : 'btn-outline-danger'
                            "
                            :disabled="removingDocumentId === doc.id"
                            @click="removeDocument(doc)"
                          >
                            <span
                              v-if="removingDocumentId === doc.id"
                              class="spinner-border spinner-border-sm me-1"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            <font-awesome-icon
                              v-else
                              :icon="['fas', 'trash']"
                              class="me-1"
                            />
                            {{
                              confirmRemovalId === doc.id
                                ? $t("common.confirm")
                                : $t("common.remove")
                            }}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Empty state -->
                  <div v-else class="text-center py-5">
                    <font-awesome-icon
                      :icon="['fas', 'file-slash']"
                      class="text-muted fs-1 mb-3 d-block"
                    />
                    <p class="text-muted mb-0">
                      {{ $t("cancellation.details.no_documents") }}
                    </p>
                  </div>

                  <!-- Admin-only: attach additional documents (e.g. the signed statement) -->
                  <div v-if="is_admin" class="mt-3 pt-3 border-top">
                    <input
                      ref="fileInputRef"
                      type="file"
                      multiple
                      class="d-none"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg"
                      @change="handleFileInputChange"
                    />
                    <div
                      v-if="selectedFiles.length > 0"
                      class="d-flex flex-column gap-1 mb-2"
                    >
                      <div
                        v-for="(file, index) in selectedFiles"
                        :key="`${file.name}-${file.size}`"
                        class="d-flex align-items-center justify-content-between small"
                      >
                        <span class="text-truncate">{{ file.name }}</span>
                        <button
                          type="button"
                          class="btn btn-sm btn-link text-danger p-0"
                          :disabled="isUploadingDocuments"
                          @click="removeSelectedFile(index)"
                        >
                          <font-awesome-icon :icon="['fas', 'times']" />
                        </button>
                      </div>
                    </div>
                    <div class="d-flex gap-2">
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-secondary"
                        :disabled="isUploadingDocuments"
                        @click="fileInputRef?.click()"
                      >
                        <font-awesome-icon
                          :icon="['fas', 'plus']"
                          class="me-1"
                        />
                        {{ $t("cancellation.details.add_documents") }}
                      </button>
                      <button
                        v-if="selectedFiles.length > 0"
                        type="button"
                        class="btn btn-sm btn-primary"
                        :disabled="isUploadingDocuments"
                        @click="uploadSelectedDocuments"
                      >
                        <span
                          v-if="isUploadingDocuments"
                          class="spinner-border spinner-border-sm me-1"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        <font-awesome-icon
                          v-else
                          :icon="['fas', 'cloud-upload']"
                          class="me-1"
                        />
                        {{ $t("cancellation.details.upload_selected") }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Download all documents button -->
          <div
            v-if="cancellationDetails?.uploaded_documents?.length > 1"
            class="d-flex justify-content-end mt-3"
          >
            <button
              type="button"
              class="btn btn-primary"
              @click="downloadAllDocuments"
              :disabled="isDownloadingAll"
            >
              <span
                v-if="isDownloadingAll"
                class="spinner-border spinner-border-sm me-1"
                role="status"
                aria-hidden="true"
              ></span>
              <font-awesome-icon
                v-else
                :icon="['fas', 'download']"
                class="me-1"
              />
              {{ $t("cancellation.details.download_all") }}
            </button>
          </div>
        </div>
      </div>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import { promiseTimeout } from "@vueuse/core";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import ModalComponent from "@/components/common/ModalComponent.vue";
import { formatDateTimeIntoDate } from "@/composables/formatUtils";
import { goSolidApi } from "@/composables/useGoSolidApi";
import type { Company } from "@/types";
import type {
  CancellationDocument,
  CancellationStatement,
} from "@/types/cancellation";
import {
  downloadFromApiResponse,
  extractFilenameFromUrl,
} from "@/utils/downloadUtils";
import { validateCancellationFiles } from "./cancellationFileValidation";
import { onMounted, reactive, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

// Props
const props = defineProps<{
  cancellationId: number;
  company_id?: number | null;
  companies?: Company[];
  is_admin: boolean;
  /**
   * Render this statement instead of fetching it by id. Used by client-only surfaces (the
   * Compliance Groups demo) whose rows have no backing API. Document upload and removal are
   * admin-only controls, so a non-admin preloaded view is read-only.
   */
  preloadedStatement?: CancellationStatement | null;
}>();

// Emits
const emit = defineEmits<{
  close: [];
}>();

const { t } = useI18n();
const { globalErrorHandler } = useGlobalHandlers();

// Refs
const modalRef =
  useTemplateRef<InstanceType<typeof ModalComponent>>("modalRef");

// State
const cancellationDetails = ref<CancellationStatement | null>(null);
const isLoading = ref(false);
const error = ref<string | null>(null);
const isDownloading = reactive<Record<number, boolean>>({});
const isDownloadingAll = ref(false);
const fileInputRef = useTemplateRef<HTMLInputElement>("fileInputRef");
const selectedFiles = ref<File[]>([]);
const isUploadingDocuments = ref(false);
const confirmRemovalId = ref<number | null>(null);
const removingDocumentId = ref<number | null>(null);

// Methods
const formatDate = (dateString: string) => {
  if (!dateString) return "-";
  return formatDateTimeIntoDate(dateString);
};

const formatBeneficiaryType = (type: string) => {
  if (!type) return "-";
  return t(`go_cancel.form.beneficiary_type.${type}`, type);
};

const getFileIcon = (fileUrl: string) => {
  if (!fileUrl) return ["fas", "file"];

  const filename = extractFilenameFromUrl(fileUrl).toLowerCase();

  if (filename.includes(".pdf")) return ["fas", "file-pdf"];
  if (filename.includes(".doc") || filename.includes(".docx"))
    return ["fas", "file-word"];
  if (filename.includes(".xls") || filename.includes(".xlsx"))
    return ["fas", "file-excel"];
  if (
    filename.includes(".png") ||
    filename.includes(".jpg") ||
    filename.includes(".jpeg")
  )
    return ["fas", "file-image"];
  if (filename.includes(".txt")) return ["fas", "file-alt"];
  if (filename.includes(".csv")) return ["fas", "file-csv"];

  return ["fas", "file"];
};

const downloadDocument = async (doc: CancellationDocument, index?: number) => {
  try {
    if (index !== undefined) {
      isDownloading[index] = true;
    }

    const companyId = props.company_id || props.companies?.[0]?.id;
    if (!companyId) {
      throw new Error("No valid company ID found");
    }

    // Use the proper download API endpoint
    const response = await goSolidApi.get(
      `/api/company/${companyId}/download/${doc.id}`,
      {
        responseType: "blob",
      }
    );

    // Use utility function to handle download
    downloadFromApiResponse(
      response.data,
      response.headers,
      extractFilenameFromUrl(doc.file_url)
    );
  } catch (error) {
    console.error("Download failed:", error);
    globalErrorHandler(error);
  } finally {
    if (index !== undefined) {
      isDownloading[index] = false;
    }
  }
};

const downloadAllDocuments = async () => {
  if (!cancellationDetails.value?.uploaded_documents?.length) return;

  isDownloadingAll.value = true;

  try {
    // Download each document with a small delay to avoid overwhelming the browser
    for (
      let i = 0;
      i < cancellationDetails.value.uploaded_documents.length;
      i++
    ) {
      const doc = cancellationDetails.value.uploaded_documents[i];
      await downloadDocument(doc, i);

      // Small delay between downloads
      if (i < cancellationDetails.value.uploaded_documents.length - 1) {
        await promiseTimeout(500);
      }
    }
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isDownloadingAll.value = false;
  }
};

const fetchCancellationDetails = async () => {
  if (!props.company_id && (!props.companies || props.companies.length === 0)) {
    error.value = "No company information available";
    return;
  }

  const companyId = props.company_id || props.companies?.[0]?.id;
  if (!companyId) {
    error.value = "No valid company ID found";
    return;
  }

  isLoading.value = true;
  error.value = null;

  try {
    const response = await goSolidApi.get(
      `/api/company/${companyId}/cancellations/${props.cancellationId}`
    );

    if (response.data?.data) {
      cancellationDetails.value = response.data.data;
    } else {
      error.value = t("cancellation.details.load_error");
    }
  } catch (err) {
    globalErrorHandler(err);
    error.value = t("cancellation.details.load_error");
  } finally {
    isLoading.value = false;
  }
};

const removeDocument = async (doc: CancellationDocument) => {
  // Two-step inline confirm: the first click arms the button, the second removes.
  if (confirmRemovalId.value !== doc.id) {
    confirmRemovalId.value = doc.id;
    return;
  }

  const companyId = props.company_id || props.companies?.[0]?.id;
  if (!companyId) return;

  removingDocumentId.value = doc.id;
  try {
    const response = await goSolidApi.delete(
      `/api/company/${companyId}/cancellations/${props.cancellationId}/documents/${doc.id}`
    );
    if (response.data?.data) {
      cancellationDetails.value = response.data.data;
    }
  } catch (err) {
    globalErrorHandler(err);
  } finally {
    removingDocumentId.value = null;
    confirmRemovalId.value = null;
  }
};

const handleFileInputChange = (event: Event) => {
  const files = (event.target as HTMLInputElement).files;
  if (!files) return;

  const validFiles = validateCancellationFiles(
    Array.from(files),
    (translationKey) => globalErrorHandler(t(translationKey))
  );

  validFiles.forEach((file) => {
    const exists = selectedFiles.value.some(
      (existing) => existing.name === file.name && existing.size === file.size
    );
    if (!exists) {
      selectedFiles.value.push(file);
    }
  });

  if (fileInputRef.value) {
    fileInputRef.value.value = "";
  }
};

const removeSelectedFile = (index: number) => {
  selectedFiles.value.splice(index, 1);
};

const uploadSelectedDocuments = async () => {
  const companyId = props.company_id || props.companies?.[0]?.id;
  if (!companyId || selectedFiles.value.length === 0) return;

  isUploadingDocuments.value = true;

  try {
    const uploadedFileIds: number[] = [];
    for (const file of selectedFiles.value) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_source", "cancellation_statement");

      const uploadResponse = await goSolidApi.post(
        `/api/company/${companyId}/upload`,
        formData
      );

      const uploadId = uploadResponse.data?.data?.upload_id;
      if (!uploadId) {
        throw new Error(`Upload failed for ${file.name}`);
      }
      uploadedFileIds.push(uploadId);
    }

    if (uploadedFileIds.length === 0) return;

    const response = await goSolidApi.post(
      `/api/company/${companyId}/cancellations/${props.cancellationId}/documents`,
      { file_upload_ids: uploadedFileIds }
    );

    if (response.data?.data) {
      cancellationDetails.value = response.data.data;
    }
    selectedFiles.value = [];
  } catch (err) {
    globalErrorHandler(err);
  } finally {
    isUploadingDocuments.value = false;
  }
};

const handleClose = () => {
  emit("close");
};

// Lifecycle
onMounted(() => {
  modalRef.value?.toggleModal();
  if (props.preloadedStatement) {
    cancellationDetails.value = props.preloadedStatement;
    return;
  }
  fetchCancellationDetails();
});
</script>
